import {inject, observer} from "mobx-react";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
// local
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {ConfirmModal} from "components/ConfirmModal";
import {ContractCreateInfo} from "components/contractProcess/ContractCreateInfo/ContractCreateInfo";
import {ContractSignatories} from "components/contractProcess/ContractSignatories";
import {ContractViewInfo} from "components/contractProcess/ContractViewInfo";
import {SignedContractSignatories} from "components/contractProcess/SignedContractSignatories";
import {IconBriefcase, IconDocumentPen, IconLock} from "components/icons";
import LoadDocsColumn from "components/LoadDocsColumn";
import {LockValue} from "components/LockValue";
import RequestDocuments from "components/requestCard/RequestDocuments";
import {Tabs} from "components/Tabs";
import {TitleIcon} from "components/TitleIcon";
import {roles} from "constants/auth.const";
import {revisionDocuments, rfpContractRevisionName} from "constants/buyer.consts";
import {contractModalNames} from "constants/modal.conts";
import {CONTRACTS, PROJECTS, toProjectId, toOfferId} from "constants/routes.const";
import {contractBuyerDocumentTabs} from "constants/tabs.const";
import {
  getSelectListMembers,
  hasPermission,
  hasUserPermission,
  removeFilesConfirmModal,
  saveDocument,
} from "helpers/helper";
import {showSuccess} from "helpers/notifications.helper";
import {fileExtension, fileType} from "constants/file.consts";

export const Contract = inject("store")(
  observer(({store: {buyer, clientStore, organization: organizationStore, auth}}) => {
    const {organizationName, user, clientRole} = auth;
    const {language} = clientStore;
    const {
      currentOffer,
      currentRequest,
      contractOwnPersons,
      contractSellerPersons,
      proposalWithContract,
      isFinishedOwnSignatures,
      isFinishedOtherSignatures,
      isFinishedContract,
      isContract,
      currentContract,
      contractId,
    } = buyer;
    const [creatingContract, setCreatingContract] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [clearingSignature, setClearingSignature] = useState(false);
    const [signatoryMembers, setSignatoryMembers] = useState([]);

    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const backPath = useMemo(
      () =>
        location.pathname.includes("contracts")
          ? CONTRACTS
          : currentRequest.ProjectId
          ? toProjectId(currentRequest.ProjectId)
          : PROJECTS,
      [location.pathname, currentRequest.ProjectId]
    );

    const canDeleteSignatory =
      contractOwnPersons?.length > 1 &&
      (hasUserPermission([roles.ADMIN, roles.OWNER], clientRole) ||
        user.id === currentRequest.ContactPersonId);

    const isDraftContract = !!currentOffer && !(currentOffer?.Contract || isContract);
    const offerFiles = useMemo(
      () =>
        isDraftContract
          ? currentOffer?.OfferFiles
          : currentOffer?.OfferFiles ?? currentContract?.Offer?.OfferFiles,
      [isDraftContract, currentOffer, currentContract]
    );
    const otherOrganizationName = (currentOffer?.Proposal ?? proposalWithContract)
      ?.Organization?.name;

    const clickRemoveSellerDocs = (id) =>
      removeFilesConfirmModal(id, (id) => removeSellerDocument(id));

    const removeSellerDocument = (id) => {
      buyer.updateOffer({
        ...currentOffer,
        OfferFiles: id ? currentOffer.OfferFiles.filter((item) => item.id !== id) : [],
      });
    };

    const clickCreateContract = async () => {
      if (!hasPermission(["CREATE_BUYER_CONTRACT"])) return;
      setCreatingContract(true);
      buyer
        .createContract({
          OfferId: currentOffer.id,
          price: +currentRequest.contractPrice,
          additions: currentRequest.contractDescription,
          offerDocuments: currentOffer.OfferFiles?.map((item) => item.File.id) ?? [],
          requestDocuments: currentRequest.documents,
        })
        .then(() => showSuccess())
        .finally(() => setCreatingContract(false));
    };

    const clickCancelContract = () =>
      ConfirmModal({
        title: t("CANCEL_CONTRACT_CONFIRM_TITLE"),
        text: t("CANCEL_CONTRACT_CONFIRM_DESCRIPTION"),
        type: "warning",
        okText: "OK",
        onOk: () =>
          navigate(toOfferId(currentRequest?.id, currentOffer.id), {replace: true}),
      });

    const onAddSignatory = (values) =>
      buyer.addSignatoryOnContract(contractId, values.ContactPersonId);

    const onDeleteSignatory = (id) => buyer.removeSignatoryOnContract(contractId, id);

    const onSubmit = (values) => {
      auth.setSignContractSideData(contractId, "buyer", values.UserId);
      auth.updateAuthModalName(contractModalNames.identifyContractPerson);
    };

    const unlockAndCleanSignatures = () => {
      if (!hasPermission(["DELETE_BUYER_CONTRACT"])) return;
      ConfirmModal({
        title: t("CANCEL_CONTRACT_CONFIRM_TITLE"),
        text: t("CANCEL_CONTRACT_CONFIRM_DESCRIPTION"),
        type: "warning",
        okText: "OK",
        onOk: async () => {
          setClearingSignature(true);
          await buyer.removeContract(contractId);
          const initialContractInfo = {
            contractPrice: currentContract?.price ?? currentOffer?.price ?? null,
            contractDescription: currentContract?.additions ?? "",
            recipients: {
              ...currentRequest.recipients,
              public: currentRequest.recipients.public.map((item) =>
                item.id === proposalWithContract?.id
                  ? {...item, Offer: {...item.Offer, Contract: null}}
                  : item
              ),
              registered: currentRequest.recipients.registered.map((item) =>
                item.id === proposalWithContract?.id
                  ? {...item, Offer: {...item.Offer, Contract: null}}
                  : item
              ),
            },
            documents: currentRequest.documents.map((item) =>
              item.name === rfpContractRevisionName
                ? {...item, id: window.crypto.randomUUID(), ...revisionDocuments}
                : item
            ),
          };
          buyer.updateCurrentRequestInfo(initialContractInfo);
          buyer.updateOffer({...currentOffer, Contract: null});
          setClearingSignature(false);
          showSuccess(t("CANCEL_CONTRACT_INFO_MESSAGE"));
        },
      });
    };

    const loadPDFWatermarkContract = async (url) => {
      let fileName = `contract-${currentContract.id}.pdf`;
      const data = await buyer.downloadPdfWatermarkContract(currentContract.id, url);
      if (!data) return;
      saveDocument(data, fileType[fileExtension.PDF], fileName, t("FILE_DOWNLOADED"));
    };

    useEffect(() => {
      contractId && buyer.getContract(contractId).catch(() => navigate(backPath));
    }, [currentOffer, contractId, buyer, navigate, backPath]);

    useEffect(() => {
      if (isContract && !isFinishedOtherSignatures) {
        const checkSellerSign = setInterval(() => {
          buyer.getContract(currentContract?.id);
        }, 3000);
        return () => clearInterval(checkSellerSign);
      }
    }, [buyer, isFinishedOtherSignatures, currentContract, isContract]);

    useEffect(() => {
      isContract &&
        organizationStore
          .getOrganizationMembers("SHOW_RFT_CONTRACT_SIGNATORIES_DROPDOWN_USER", true)
          .then((members) => setSignatoryMembers(getSelectListMembers(members)));
    }, [organizationStore, isContract]);

    return (
      <div className="contract-tab_wrapper">
        {isContract ? (
          <ContractViewInfo
            price={currentContract?.price?.toLocaleString(language ?? "sv")}
            currencyCode={currentRequest.CurrencyCode}
            additions={currentContract?.additions}
          />
        ) : (
          <div className="mt-12 mb-8">
            <ContractCreateInfo />
          </div>
        )}
        <div className="contract-tab_documents-tabs-title">
          <TitleIcon
            title={t("DOCUMENTS")}
            addClass="documents-title"
            icon={<IconBriefcase />}
          />
          <Tabs
            tabs={contractBuyerDocumentTabs}
            active={activeTab}
            setActive={(id) => setActiveTab(id)}
          />
        </div>
        {activeTab === 0 && (
          <RequestDocuments
            revisions={currentRequest?.documents}
            editable={isDraftContract}
            columnTitles={{
              contracts: "CONTRACT",
              documents: "DOCUMENTS",
              plans: "PLANS",
              links: "LINKS",
            }}
            columnAddPermissions={{
              links: ["ENABLE_RFT_DOCUMENTS_ADD_LINKS"],
            }}
            customLoadDocument={{
              contracts:
                isFinishedContract &&
                !currentContract?.isDocumentsReady &&
                loadPDFWatermarkContract,
            }}
            needZipButton
            intoContract={isDraftContract}
            disableZipButton={isFinishedContract && !currentContract?.isDocumentsReady}
            allRevisionsView
            showRevisionNameInColumn={isDraftContract}
          />
        )}
        {activeTab === 1 && (
          <div className="contract-tab_documents">
            <LoadDocsColumn
              title="DOCUMENTS"
              removable={isDraftContract}
              titleIcon={<IconDocumentPen />}
              button="ADD_DOCUMENT"
              needZipButton={isContract}
              docs={offerFiles?.filter((file) => file.inContract) ?? []}
              removeFile={clickRemoveSellerDocs}
              canRemoveAllFiles
            />
          </div>
        )}

        {!isFinishedContract && (
          <div className="contract-tab_buttons">
            {isContract && (
              <LockValue
                canPermissions={["DELETE_BUYER_CONTRACT"]}
                needLabel
                needShowModalToSubscribe
              >
                <ButtonTransparent
                  name={t("UNLOCK_DOCUMENTS")}
                  type="button"
                  small
                  icon={<IconLock />}
                  color="red"
                  loading={clearingSignature}
                  onClick={unlockAndCleanSignatures}
                />
              </LockValue>
            )}
            {isDraftContract && (
              <ButtonTransparent
                name={t("CANCEL")}
                type="button"
                addClass="mr-8"
                onClick={clickCancelContract}
              />
            )}
            {isDraftContract && (
              <LockValue
                canPermissions={["CREATE_BUYER_CONTRACT"]}
                needLabel
                needShowModalToSubscribe
              >
                <ButtonTransparent
                  name={t("LOCK_AND_PREPARE")}
                  nonTransparent
                  type="button"
                  loading={creatingContract}
                  onClick={clickCreateContract}
                />
              </LockValue>
            )}
          </div>
        )}
        {isContract && !isFinishedContract && (
          <ContractSignatories
            contractOwnPersons={contractOwnPersons}
            contractOtherPersons={contractSellerPersons}
            isFinishedOwnSignatures={isFinishedOwnSignatures}
            isFinishedOtherSignatures={isFinishedOtherSignatures}
            ownOrganizationName={organizationName}
            otherOrganizationName={otherOrganizationName}
            membersList={signatoryMembers.filter(
              (member) =>
                !contractOwnPersons
                  ?.map((person) => person?.UserId)
                  .includes(member.value)
            )}
            currentUser={user}
            canDeleteMember={canDeleteSignatory}
            onSubmit={onSubmit}
            onAddSignatory={onAddSignatory}
            onDeleteSignatory={onDeleteSignatory}
            onSendMessage={() => buyer.setActiveRequestTab(4)}
          />
        )}

        {isFinishedContract && (
          <SignedContractSignatories
            contractOwnPersons={contractOwnPersons}
            contractOtherPersons={contractSellerPersons}
            ownOrganizationName={organizationName}
            otherOrganizationName={otherOrganizationName}
            contractId={contractId}
          />
        )}
      </div>
    );
  })
);
